import {
  Checkbox,
  Grid,
  IconButton,
  IconName,
  Text,
  TextHighlight,
  TextSize,
  TextWeight,
  TipPlacement,
  Tooltip
} from '@focaldata/cin-ui-components'
import React from 'react'
import {
  FilterResponseOption,
  ForkFilterOption,
  NewFilterOption,
  SelectedFilter
} from '../../data/model/results'
import { truncateText } from '../../utils/questionnaireUtils'
import useStyles from '../ResultsFilterSiderbar/FilterBlock/FilterBlock.styles' // Reuse existing styles

export interface AnalyticsResponseOptionSelection {
  isFork: boolean
  responseOptionId: string
  filterId: string // questionId for questions, forkId for forks
  // Disambiguates a questionId split across market clusters in the combined view.
  marketLabel?: string | null
}

interface Props {
  filter: NewFilterOption | ForkFilterOption
  selectedFilter: SelectedFilter
  isDisabled?: boolean
  onDelete?: (
    filterId: string,
    isFork: boolean,
    marketLabel?: string | null
  ) => void
  onToggleResponseOption: (selection: AnalyticsResponseOptionSelection) => void
}

const isQuestionFilter = (
  filter: NewFilterOption | ForkFilterOption
): filter is NewFilterOption => {
  return 'questionId' in filter
}

const isForkFilter = (
  filter: NewFilterOption | ForkFilterOption
): filter is ForkFilterOption => {
  return 'id' in filter && !('questionId' in filter)
}

const AnalyticsFilterBlock: React.FC<Props> = (props: Props) => {
  const {
    filter,
    selectedFilter,
    isDisabled,
    onDelete,
    onToggleResponseOption
  } = props
  const { classes } = useStyles()

  const filterId = isQuestionFilter(filter) ? filter.questionId : filter.id
  const isFork = isForkFilter(filter)
  const marketLabel = isQuestionFilter(filter) ? filter.marketLabel : undefined

  const onClickDelete = () => {
    if (onDelete) {
      onDelete(filterId, isFork, marketLabel)
    }
  }

  const isResponseOptionSelected = (responseOptionId: string): boolean => {
    if (isFork) {
      return selectedFilter.branchNumbers?.includes(responseOptionId) || false
    }
    return selectedFilter.responseOptionIds?.includes(responseOptionId) || false
  }

  const handleResponseOptionToggle = (responseOptionId: string) => {
    onToggleResponseOption({
      isFork,
      responseOptionId,
      filterId,
      marketLabel
    })
  }

  return (
    <>
      <Grid
        container
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        key={filterId}
      >
        <Grid item container xs={11}>
          <Grid
            item
            xs="auto"
            alignItems="flex-start"
            justifyContent="flex-start"
          />
          <Grid item xs={11}>
            <Text
              className={classes.title}
              size={TextSize.ms}
              weight={TextWeight.SemiBold}
              highlight={TextHighlight.Background}
            >
              {filter.text}
            </Text>
          </Grid>
        </Grid>
        <Grid
          item
          xs={1}
          container
          className={classes.deleteIcon}
          alignItems="flex-start"
          justifyContent="flex-end"
        >
          <IconButton
            iconName={IconName.DeleteOutline}
            onClick={onClickDelete}
            disabled={isDisabled}
          />
        </Grid>
      </Grid>
      <Grid container>
        {filter.responseOptions.map((responseOption) => {
          const completes = isFork
            ? undefined
            : (responseOption as FilterResponseOption).nWeightedCompletes ||
              (responseOption as FilterResponseOption).nCompletes

          // Format weighted completes as integers (rounded, with commas)
          const formattedTotal =
            completes !== null && completes !== undefined
              ? Math.round(completes).toLocaleString()
              : undefined

          const isChecked = isResponseOptionSelected(responseOption.id)

          const MAX_CHARACTERS_CHECKBOX_LABEL = 45

          return (
            <Grid item container xs={12} wrap="nowrap" key={responseOption.id}>
              <Grid container className={classes.root}>
                <Grid item xs className={classes.checkBox}>
                  <Tooltip
                    title={responseOption.text}
                    tipPlacement={TipPlacement.Top}
                  >
                    <Checkbox
                      isGrouped
                      label={truncateText(
                        MAX_CHARACTERS_CHECKBOX_LABEL,
                        responseOption.text
                      )}
                      disabled={isDisabled}
                      className={classes.fullWidth}
                      defaultChecked={isChecked}
                      onChange={() =>
                        handleResponseOptionToggle(responseOption.id)
                      }
                    />
                  </Tooltip>
                </Grid>
                <Grid
                  item
                  xs="auto"
                  alignItems="center"
                  className={classes.numbers}
                >
                  {!isFork && formattedTotal !== undefined && (
                    <Text
                      size={TextSize.ms}
                      highlight={TextHighlight.Background}
                    >
                      ({formattedTotal})
                    </Text>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )
        })}
      </Grid>
    </>
  )
}

export default AnalyticsFilterBlock
